import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import "./ActivatePage.css";

const ActivatePage = () => {
  const { uid, token } = useParams();
  const [message, setMessage] = useState("Activating your account...");
  const [isLoading, setIsLoading] = useState(true);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const activateAccount = async () => {
      try {
        const response = await axios.post(
          `${SERVER_URL}/api/auth/users/activation/`,
          { uid, token }
        );

        if (isMounted) {
          setIsLoading(false);
          setMessage("Account activated successfully! Redirecting...");
          setTimeout(() => navigate("/signin"), 3000);
        }
      } catch (err) {
        const errorDetail = err.response?.data?.detail || "Unknown error";

        if (isMounted) {
          setIsLoading(false);
          if (errorDetail === "Stale token for given user.") {
            setMessage("Account is successfully activated! Redirecting...");
            setTimeout(() => navigate("/signin"), 3000);
          } else {
            setMessage(`Activation failed: ${errorDetail}`);
          }
        }
      }
    };

    activateAccount();

    return () => {
      isMounted = false;
    };
  }, [uid, token, navigate, SERVER_URL]);

  return (
    <div className="activate-container">
      <Helmet>
        <title>Activate Your Account – Mend Grove</title>
        <meta
          name="description"
          content="Activate your Mend Grove account to start your brain-training exercises. Secure, fast, and easy account activation."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://mendgrove.com/activate" />
        <meta property="og:title" content="Activate Your Account – Mend Grove" />
        <meta
          property="og:description"
          content="Activate your Mend Grove account and start your brain-training exercises."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mendgrove.com/activate" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Activate Your Account – Mend Grove" />
        <meta
          name="twitter:description"
          content="Activate your Mend Grove account and start your brain-training exercises."
        />
        <meta name="twitter:image" content="https://mendgrove.com/logo192.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Mend Grove",
              "url": "https://mendgrove.com/",
              "logo": "https://mendgrove.com/logo192.png",
              "sameAs": [
                "https://www.linkedin.com/company/mendgrove",
                "https://www.facebook.com/mendgrove"
              ],
              "description": "Mend Grove offers gentle, science-based brain-training games to support cognitive recovery and everyday mental exercise.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@mendgrove.com",
                "contactType": "customer support",
                "url": "https://mendgrove.com/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="activate-card" role="alert" aria-live="polite">
        <h2 className="activate-title">Account Activation</h2>
        <div className="activate-message">
          {isLoading ? (
            <div className="activate-loading">
              <FaSpinner className="animate-spin" />
              <span>{message}</span>
            </div>
          ) : (
            <p>{message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivatePage;
